import { useProjectStore, useSurveyStore } from '@/lib/zustand/useStore';
import { useRef } from 'react';

import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { QuestionForm, questionFormDefaultValues, QuestionFormRef, QuestionFormSchemaType } from '@/features/question/components/organisms/QuestionForm';
import { useAccessImperativeHandle } from '@/hooks/useAccessImperativeHandle';
import { EditQuestionModalProps } from '.';
import { useGetQuestionsBySurveyId } from '@/features/question/hooks/useQuery/useGetQuestionsBySurveyId';

export function EditQuestionModal({
    show,
    onHide,
    onSubmit,
    isQuestionPropertiesDisabled = false,
    questionData,
    crm,
    crmProject,
    crmConfig,
    crmSystem,
    questionsFromOutside,
}: Readonly<EditQuestionModalProps>) {
    const questionFormRef = useRef<QuestionFormRef>(null);
    const resolvedQuestionFormRef = useAccessImperativeHandle(questionFormRef)
    const { project } = useProjectStore();
    crm = crm || project?.crm_project?.crm || undefined;
    crmProject = crmProject || project?.crm_project || undefined;

    const { survey } = useSurveyStore();

    const { data: questions } = useGetQuestionsBySurveyId({
        surveyId: survey?.id,
        isArchived: false,
        sortByCreatedAtAscending: false,
    });

    const onCloseQuestionModal = () => {
        resolvedQuestionFormRef?.resetForm && resolvedQuestionFormRef?.resetForm();
        onHide();
    }

    const onSubmitQuestionForm = async (values: QuestionFormSchemaType) => {
        onSubmit(values);
        resolvedQuestionFormRef?.resetForm && resolvedQuestionFormRef?.resetForm();
    }

    const filteredQuestions = questionsFromOutside ? questionsFromOutside?.filter((question) => question.id !== questionData?.id) : questions?.questions?.filter((question) => question.id !== questionData?.id);
    return (
        <Dialog open={show} onOpenChange={onCloseQuestionModal} defaultOpen={false} >
                <DialogContent className="max-w-6xl">
                    <DialogHeader>
                        <DialogTitle className='ps-3'>
                            Question Parameters
                        </DialogTitle>
                    </DialogHeader>

                    <ScrollArea className="max-h-[60vh] sm:max-h-[70vh] px-3">
                        <hr className="mb-3" />

                        <div className={`px-1`}>
                            <QuestionForm
                                defaultValues={questionData || questionFormDefaultValues}
                                ref={questionFormRef}
                                crm={crm}
                                crmProject={crmProject}
                                crmConfig={crmConfig}
                                isFormDisabled={isQuestionPropertiesDisabled}
                                questions={filteredQuestions}
                                crmSystem={crmSystem || project?.crm_system}
                            />
                        </div>

                    </ScrollArea>
                    <DialogFooter className="px-3">
                        <div className="flex items-stretch justify-end px-1 m-0 sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                            <Button size="sm" variant="secondary" type="button" onClick={onCloseQuestionModal}>Close</Button>
                            <Button size="sm" type="button" onClick={() => {
                                resolvedQuestionFormRef?.form?.handleSubmit(onSubmitQuestionForm, err => {
                                    console.error(err);
                                    ErrorPopup("Form submission error")
                                })()
                            }}>
                                Submit
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
    )
}
